import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { applySeoCustom } from "../lib/seo";
import { MaskedLines, Reveal, SectionHeading } from "../components/site/Reveal";
import { CTABand } from "../components/site/CTABand";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AreasIndex() {
  const [areas, setAreas] = useState([]);

  useEffect(() => {
    applySeoCustom({
      title: "Areas We Serve | Aluminium Windows & Doors Surrey, Berkshire & Hampshire | Cedar Views",
      description: "Cedar Views installs premium aluminium windows and doors across Surrey, Berkshire and Hampshire. Find your local area page for showrooms, projects and advice.",
      path: "/areas/",
    });
    fetch(`${API}/api/areas`).then((r) => r.json()).then(setAreas).catch(() => {});
    window.scrollTo(0, 0);
  }, []);

  const counties = ["Surrey", "Berkshire", "Hampshire"];
  const grouped = counties
    .map((c) => ({ county: c, items: areas.filter((a) => a.county === c) }))
    .filter((g) => g.items.length > 0);
  const extra = areas.filter((a) => !counties.includes(a.county));
  if (extra.length) grouped.push({ county: "Further afield", items: extra });

  return (
    <main data-testid="areas-index">
      <section className="pt-44 pb-16 bg-[#050A15]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal>
            <p className="overline-x mb-5">
              <Link to="/" className="hover:text-white transition-colors">Home</Link>
              <span className="mx-2">/</span>Areas
            </p>
          </Reveal>
          <MaskedLines as="h1" lines={["Areas", "We Serve."]} delay={0.15}
            className="font-display tracking-tighter text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.02]" />
          <Reveal delay={0.5} className="mt-7">
            <h2 className="text-base md:text-lg text-white/70 max-w-2xl font-normal">
              Premium aluminium windows and doors, designed, supplied and installed by one in-house team
              across Surrey, Berkshire and Hampshire.
            </h2>
          </Reveal>
        </div>
      </section>

      {grouped.map((g, gi) => (
        <section key={g.county} className={gi % 2 ? "bg-[#050A15]" : "bg-[#070E1D] border-t hairline"}>
          <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-16 lg:py-20">
            <SectionHeading number={String(gi + 1).padStart(2, "0")} kicker="County" title={`${g.county}.`} />
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-testid={`areas-${g.county.toLowerCase()}`}>
              {g.items.map((a, i) => (
                <Reveal key={a.slug} delay={(i % 4) * 0.05}>
                  <Link to={`/areas/${a.slug}/`} data-testid={`area-link-${a.slug}`}
                    className="group flex items-center justify-between border border-white/12 px-5 py-4 hover:bg-white hover:text-[#0B162C] text-white transition-colors">
                    <span className="font-display font-medium text-base">{a.town}</span>
                    <ArrowRight size={14} className="opacity-40 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      ))}

      <CTABand product="Windows & Doors" />
    </main>
  );
}
